import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { RealtimeHubService } from '../../core/hubs/realtime/realtime-hub.service';
import {
  REALTIME_ORDER_HUB_STATUS,
  resolveRealtimeOrderHubStatus,
  type OrderRealtimePayload,
} from '../../core/hubs/realtime/models/order-realtime.model';
import { AppPaths } from '../../core/router/app-paths';
import { UserSessionService } from '../../core/services/user-session.service';

/**
 * Sidebar badge for new orders received via SignalR while the user is not on the orders page.
 * Count resets when the user navigates to {@link AppPaths.orders}.
 */
@Injectable({ providedIn: 'root' })
export class OrdersBadgeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeHub = inject(RealtimeHubService);
  private readonly session = inject(UserSessionService);
  private readonly router = inject(Router);

  /** Unread new-order count shown on the sidebar Orders link. */
  readonly count = signal(0);

  constructor() {
    this.realtimeHub
      .on$('OrderRealtime')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.onOrderRealtime(payload));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isOnOrdersRoute()) {
          this.clear();
        }
      });

    if (this.isOnOrdersRoute()) {
      this.clear();
    }
  }

  clear(): void {
    this.count.set(0);
  }

  private onOrderRealtime(payload: OrderRealtimePayload): void {
    if (!this.session.user()) return;
    if (this.isOnOrdersRoute()) return;
    if (!payload.id && !payload.orderNumber) return;
    if (resolveRealtimeOrderHubStatus(payload) !== REALTIME_ORDER_HUB_STATUS.Created) return;

    this.count.update((current) => current + 1);
  }

  private isOnOrdersRoute(): boolean {
    const segments = this.router.parseUrl(this.router.url).root.children['primary']?.segments ?? [];
    const first = segments[0]?.path ?? '';
    return first === AppPaths.orders;
  }
}
